import {
  useV2CreateRoleMutation,
  useV2UpdateRoleMutation,
  useV2DeleteRoleMutation,
} from "@generated";
import type { V2RoleResource } from "@generated";
import type { RoleFormValues } from "../types";

// Build the JSON:API `data` body for create/update. The `account` relationship
// required by V2RoleResource is inferred from auth server-side, so we omit it
// and cast (same pattern as the SalesExecutives/BusinessUnits MSPAs).
//
// `privileges` is stored/returned as a hash { "scope.resource": level } (the raw
// jsonb column), NOT the array the generated swagger type claims. Blank (unset)
// selections are dropped.
export const buildRoleData = (
  values: RoleFormValues,
  id?: string
): V2RoleResource => {
  const privileges: Record<string, string> = {};
  Object.entries(values.privileges).forEach(([privilege, level]) => {
    if (level !== "") privileges[privilege] = level;
  });

  return {
    type: "roles",
    ...(id ? { id: Number(id) } : {}),
    attributes: {
      name: values.name,
      default: values.default,
      privileges,
    },
  } as unknown as V2RoleResource;
};

export const useRoleMutations = () => {
  const [create, createState] = useV2CreateRoleMutation();
  const [update, updateState] = useV2UpdateRoleMutation();
  const [destroy, destroyState] = useV2DeleteRoleMutation();

  return {
    create: (values: RoleFormValues) =>
      create({ body: { data: buildRoleData(values) } }).unwrap(),

    update: (id: string, values: RoleFormValues) =>
      update({
        id: Number(id),
        body: { data: buildRoleData(values, id) },
      }).unwrap(),

    // Hard delete (roles have no soft-delete / archive state).
    destroy: (id: string) => destroy({ id: Number(id) }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useRoleMutations;
